import { Alert, Button, Empty, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { MdDeleteOutline, MdOutlineAdd, MdOutlineAttachFile } from 'react-icons/md'

import { crmOwnerOf } from '@/Containers/CrmSection/Components/Owner/CrmOwnerFormContext'
import { useNotification } from '@/Core/Hooks/useNotification'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { FieldSection } from '@/Ship/Components/EntityCard'
import { Hint } from '@/Ship/Components/Hint'
import { formatDateTimeUk, userName } from '@/Ship/Helpers/fieldViews'
import { formatMoneyWithCurrency } from '@/Ship/Helpers/formatNumber'

import { TRANSPORT_OFFER_KINDS } from '../Contracts/ITransportOffer'
import { OFFER_FIELD_LABELS as L } from '../Contracts/OfferFieldLabels'
import { watchOfferCardsClosed } from '../Stores/OpenOfferCards'
import { TransportOffersBlockStore } from '../Stores/TransportOffersBlockStore'

import type { TransportOfferKind, TransportOfferRow } from '../Contracts/ITransportOffer'
import type { TableColumnsType } from 'antd'

const { Text } = Typography

/** Те, що блок бере з `formContext` картки власника: `uuid`, `@id` і запис `countOffers`. */
export interface TransportOfferOwnerFormContextLike {
    values?: { uuid?: unknown; '@id'?: unknown; [key: string]: unknown }
    setInitialValue?: (path: string, value: unknown) => void
}

export interface TransportOffersBlockProps {
    /** Власник: запит, тендер чи заявка */
    kind: TransportOfferKind
    /** `formContext` картки власника — збереженого */
    owner: TransportOfferOwnerFormContextLike | undefined
}

/** Перевізник — посилання на його картку (`SPEC-024` §8.1), поверх картки заявки. */
const CarrierLink = ({ carrier }: { carrier: TransportOfferRow['carrier'] }) => {
    if (!carrier?.uuid) return <>—</>

    const uuid = carrier.uuid

    return (
        <Typography.Link
            onClick={(event) => {
                // Клік по посиланню не відкриває картку пропозиції
                event.stopPropagation()
                OpenResourceCardTask({ resource: 'Crm.Carrier', mode: 'view', displayMode: 'drawer', primaryFieldValue: uuid })
            }}
        >
            {carrier.title ?? '—'}
        </Typography.Link>
    )
}

/** Коментар і значок кількості файлів біля нього (`SPEC-024` §8.1). */
const CommentCell = ({ row }: { row: TransportOfferRow }) => {
    const files = Array.isArray(row.files) ? row.files.length : 0

    return (
        <Space size={6} align="start">
            <span>{row.comment || '—'}</span>
            {files > 0 ? (
                <Tooltip title={`Файлів: ${files}`}>
                    <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                        <MdOutlineAttachFile /> {files}
                    </Text>
                </Tooltip>
            ) : null}
        </Space>
    )
}

/**
 * Підказка над блоком запиту й заявки (`SPEC-024` §8.4, `SPEC-025` §3.7). Одна
 * на всі картки — `id` один, тож вона тут, а не в шаблонах власників.
 *
 * Друге речення — про «Є краща ціна»: дзвіночок і лист шле
 * `ApplicationNotificationService` за подією `ApplicationOfferOutbidEvent`
 * (Н-025.3); порівняння — `OfferPriceComparator`, за курсом НБУ на сьогодні.
 */
const OffersCounterHint = () => (
    <Hint id="transport.offers.counter">
        Нові запити, тендери й заявки без вашої пропозиції рахує бейдж у меню. Коли в заявці з'являється дешевша
        пропозиція — за курсом НБУ на сьогодні, — автори попередньої найкращої отримують сповіщення.
    </Hint>
)

const buildColumns = (hasCarrier: boolean): TableColumnsType<TransportOfferRow> => [
    {
        title: L.offeredPrice,
        key: 'offeredPrice',
        render: (_: unknown, row) => (
            <span style={{ whiteSpace: 'nowrap' }}>
                {formatMoneyWithCurrency(row.offeredPrice ?? null, row.offeredPriceCurrency?.code ?? 'EUR')}
            </span>
        ),
    },
    { title: L.comment, key: 'comment', render: (_: unknown, row) => <CommentCell row={row} /> },
    { title: L.responsible, key: 'responsible', render: (_: unknown, row) => (row.responsible ? userName(row.responsible) : '—') },
    { title: L.createdAt, key: 'createdAt', render: (_: unknown, row) => formatDateTimeUk(row.createdAt) },
    ...(hasCarrier
        ? [{ title: L.carrier, key: 'carrier', render: (_: unknown, row: TransportOfferRow) => <CarrierLink carrier={row.carrier} /> }]
        : []),
    { title: L.updatedAt, key: 'updatedAt', render: (_: unknown, row) => formatDateTimeUk(row.updatedAt) },
]

/**
 * Блок «Цінові пропозиції» праворуч у картці запиту, тендера й заявки
 * (`SPEC-024` §8.1; легасі `RequestFormTemplate.jsx:63-102`,
 * `TenderFormTemplate.jsx:66-108`, `ApplicationFormTemplate.jsx:75-90`).
 *
 *  - **«Додати»** — за колекційним `_permissions.create` (L, SL, A); власник
 *    у новій пропозиції вже стоїть, вибору власника у формі немає;
 *  - **рядок** відкриває картку пропозиції другою шухлядою; «Редагувати» й
 *    «Видалити» там — за `_permissions` запису; «Видалити» в рядку — теж;
 *  - після закриття картки чи видалення блок перечитується, а `countOffers`
 *    власника (бейдж у шапці) береться з тієї ж відповіді — без окремого запиту.
 *
 * ⚠️ Власна таблиця, а не ядровий `ResourceList`: вкладений список чужого
 * ресурсу в картці підхоплює її контекст (`CrmChildListTab`, CORE-ISSUES §3).
 */
export const TransportOffersBlock = observer(({ kind, owner }: TransportOffersBlockProps) => {
    const config = TRANSPORT_OFFER_KINDS[kind]
    const [store] = useState(() => new TransportOffersBlockStore(config))
    const { showError } = useNotification()

    const ref = crmOwnerOf(owner)
    const ownerUuid = ref?.uuid

    const reload = async (): Promise<void> => {
        const total = await store.load(ownerUuid)

        if (total !== null && owner?.values?.['countOffers'] !== total) owner?.setInitialValue?.('countOffers', total)
    }

    // Підписка живе довше за рендер — читає завжди свіжий `reload`
    const reloadRef = useRef(reload)
    reloadRef.current = reload

    useEffect(() => {
        void store.load(ownerUuid)
    }, [store, ownerUuid])

    // Блок перечитується, коли закривається картка пропозиції: зміни могли
    // бути внесені не лише кнопкою «Зберегти» (файли, видалення з картки)
    useEffect(() => watchOfferCardsClosed(config.resource, () => void reloadRef.current()), [config.resource])

    if (!ref) return null

    const openCard = (mode: 'create' | 'view', uuid?: string): void => {
        OpenResourceCardTask({
            resource: config.resource,
            mode,
            displayMode: 'drawer',
            ...(uuid ? { primaryFieldValue: uuid } : {}),
            ...(mode === 'create' ? { initialValues: { [config.ownerField]: ref } } : {}),
        })
    }

    const remove = (row: TransportOfferRow): void => {
        void store.remove(row.uuid).then((error) => {
            if (error !== null) showError('Помилка', error)

            void reload()
        })
    }

    const columns: TableColumnsType<TransportOfferRow> = [
        ...buildColumns(config.hasCarrier),
        {
            title: '',
            key: 'actions',
            width: 48,
            render: (_: unknown, row: TransportOfferRow) =>
                row._permissions?.delete === true ? (
                    // Клік у колонці дій не відкриває картку рядка
                    <span onClick={(event) => event.stopPropagation()}>
                        <Popconfirm
                            title="Видалити пропозицію?"
                            description={
                                config.fileUploadUrl
                                    ? 'Пропозицію разом із файлами буде видалено безповоротно.'
                                    : 'Пропозицію буде видалено безповоротно.'
                            }
                            okText="Видалити"
                            cancelText="Скасувати"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => remove(row)}
                        >
                            <Tooltip title="Видалити">
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<MdDeleteOutline />}
                                    aria-label="Видалити пропозицію"
                                    loading={store.removingUuid === row.uuid}
                                />
                            </Tooltip>
                        </Popconfirm>
                    </span>
                ) : null,
        },
    ]

    const hidden = store.total - store.items.length

    return (
        <FieldSection
            title={L.blockTitle}
            extra={
                store.canCreate ? (
                    <Button size="small" type="primary" icon={<MdOutlineAdd />} onClick={() => openCard('create')}>
                        Додати
                    </Button>
                ) : null
            }
        >
            {kind === 'tender' ? null : <OffersCounterHint />}
            {store.failed ? <Alert type="error" showIcon title="Не вдалося завантажити цінові пропозиції." /> : null}

            {store.isLoading && store.items.length === 0 ? (
                <Spin style={{ display: 'block', margin: '24px auto' }} />
            ) : store.items.length === 0 && !store.failed ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Пропозицій ще немає." />
            ) : store.items.length === 0 ? null : (
                <Table<TransportOfferRow>
                    size="small"
                    rowKey="uuid"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    columns={columns}
                    dataSource={store.items}
                    loading={store.isLoading}
                    onRow={(row) => ({
                        style: { cursor: 'pointer' },
                        onClick: () => openCard('view', row.uuid),
                    })}
                />
            )}

            {hidden > 0 ? (
                <Text type="secondary">
                    Показано {store.items.length} пропозицій із {store.total}
                </Text>
            ) : null}
        </FieldSection>
    )
})
